import { useCallback, useEffect, useRef, useState } from "react";

// Configuration - Chunk timing
const MIN_CHUNK_DURATION_MS = 2000; // Minimum 2s before sending a chunk
const MAX_CHUNK_DURATION_MS = 10_000; // Maximum 10s, then force send
const SILENCE_TRIGGER_MS = 1500; // 1500ms silence triggers chunk send
const SILENCE_THRESHOLD_DB = -38; // dB level considered silence (ambient noise is ~-44dB)

// Configuration - MediaRecorder
const DATA_AVAILABLE_INTERVAL_MS = 500; // How often MediaRecorder emits data chunks

// Configuration - Audio level smoothing for visualization
const AUDIO_LEVEL_DECAY_RATE = 0.85; // How quickly levels decay (0-1, higher = slower decay)
const AUDIO_LEVEL_ATTACK_RATE = 0.6; // How quickly levels rise (0-1, higher = faster attack)

// Configuration - Stop recording timing
const STOP_REQUEST_DATA_DELAY_MS = 100; // Wait for requestData() to complete
const STOP_FINAL_DATA_DELAY_MS = 200; // Wait for final data after stopping recorder

export type StreamingTranscriptionState = "idle" | "recording" | "processing" | "error";

export interface UseStreamingTranscriptionOptions {
  /** Function to transcribe audio blob */
  transcribeAudio: (blob: Blob) => Promise<string>;
  /** Called when transcript updates */
  onTranscriptUpdate?: (text: string) => void;
  /** Called on error */
  onError?: (error: Error) => void;
}

export interface UseStreamingTranscriptionReturn {
  state: StreamingTranscriptionState;
  transcript: string;
  error: string | null;
  audioLevels: number[];
  startRecording: () => Promise<void>;
  /** Stops recording and returns the final transcript */
  stopRecording: () => Promise<string>;
  cancelRecording: () => void;
}

/**
 * Hook for streaming transcription with dual-recorder strategy.
 *
 * Strategy:
 * 1. Main recorder runs continuously for the full recording (final transcription)
 * 2. Chunk recorder starts fresh for each segment (each chunk gets valid WebM header)
 * 3. On silence/max duration, stop chunk recorder, send it, start new one
 * 4. On stop, send the full recording for final transcription
 */
export const useStreamingTranscription = (
  options: UseStreamingTranscriptionOptions
): UseStreamingTranscriptionReturn => {
  const { transcribeAudio, onTranscriptUpdate, onError } = options;

  const [state, setState] = useState<StreamingTranscriptionState>("idle");
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [audioLevels, setAudioLevels] = useState<number[]>([0, 0, 0, 0, 0, 0, 0, 0]);

  // Refs for recording state
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const mainRecorderRef = useRef<MediaRecorder | null>(null); // For full recording
  const chunkRecorderRef = useRef<MediaRecorder | null>(null); // For progressive chunks
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Refs for audio data
  const mainChunksRef = useRef<Blob[]>([]); // ALL audio for final transcription
  const chunkStartTimeRef = useRef<number>(0);
  const silenceStartRef = useRef<number | null>(null);
  const maxDurationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isRecordingRef = useRef<boolean>(false);
  const isCancelledRef = useRef<boolean>(false);
  const isFinalTranscriptSentRef = useRef<boolean>(false); // Prevents chunk transcripts from overwriting final
  const mimeTypeRef = useRef<string>("audio/webm");
  const hadSpeechInChunkRef = useRef<boolean>(false);
  const hadAnySpeechRef = useRef<boolean>(false); // Track if ANY speech detected in entire session
  const pendingChunkHadSpeechRef = useRef<boolean>(false); // For passing to onstop handler

  // Refs for transcription results
  const transcriptPartsRef = useRef<string[]>([]);

  // Ref for smoothing audio levels (for decay effect)
  const smoothedLevelsRef = useRef<number[]>([0, 0, 0, 0, 0, 0, 0, 0]);

  // Callback refs
  const onTranscriptUpdateRef = useRef(onTranscriptUpdate);
  const onErrorRef = useRef(onError);
  const transcribeAudioRef = useRef(transcribeAudio);

  useEffect(() => {
    onTranscriptUpdateRef.current = onTranscriptUpdate;
    onErrorRef.current = onError;
    transcribeAudioRef.current = transcribeAudio;
  }, [onTranscriptUpdate, onError, transcribeAudio]);

  // Update the displayed transcript
  const updateTranscript = useCallback((text: string) => {
    setTranscript(text);
    onTranscriptUpdateRef.current?.(text);
  }, []);

  // Send a chunk for transcription
  const sendChunkForTranscription = useCallback(
    async (audioBlob: Blob) => {
      if (audioBlob.size === 0) {
        return;
      }

      try {
        const text = await transcribeAudioRef.current(audioBlob);

        // Don't update transcript if cancelled or if final transcript has been sent
        // (prevents race condition where chunk transcript overwrites final)
        if (text && text.trim() && !isCancelledRef.current && !isFinalTranscriptSentRef.current) {
          transcriptPartsRef.current.push(text.trim());
          const combined = transcriptPartsRef.current.join(" ");
          updateTranscript(combined);
        }
      } catch {
        // Chunk transcription failed - continue with next chunk
      }
    },
    [updateTranscript]
  );

  // Ref to store current chunk's audio data
  const currentChunkDataRef = useRef<Blob[]>([]);

  // Forward declare for circular references
  const startChunkRecorderRef = useRef<(() => void) | null>(null);
  const triggerChunkEndRef = useRef<(() => void) | null>(null);

  // Start a new chunk recorder
  const startChunkRecorder = useCallback(() => {
    const stream = mediaStreamRef.current;
    if (!stream || !isRecordingRef.current) {
      return;
    }

    // Stop existing chunk recorder if any
    if (chunkRecorderRef.current && chunkRecorderRef.current.state !== "inactive") {
      chunkRecorderRef.current.stop();
    }

    // Reset chunk data
    currentChunkDataRef.current = [];

    const chunkRecorder = new MediaRecorder(stream);
    chunkRecorderRef.current = chunkRecorder;
    const mimeType = chunkRecorder.mimeType || "audio/webm";

    chunkRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        currentChunkDataRef.current.push(e.data);
      }
    };

    // onstop handles data after all ondataavailable events have fired
    chunkRecorder.onstop = () => {
      const chunkData = [...currentChunkDataRef.current];
      const hadSpeech = pendingChunkHadSpeechRef.current;

      // Send the chunk if it had speech (fire and forget - use void to indicate intentionally not awaited)
      if (hadSpeech && chunkData.length > 0 && !isCancelledRef.current) {
        const blob = new Blob(chunkData, { type: mimeType });
        void sendChunkForTranscription(blob);
      }

      // Start new chunk recorder if still recording
      if (isRecordingRef.current) {
        startChunkRecorderRef.current?.();
      }
    };

    chunkRecorder.start(DATA_AVAILABLE_INTERVAL_MS);
    chunkStartTimeRef.current = Date.now();
    hadSpeechInChunkRef.current = false;
    silenceStartRef.current = null; // Reset silence tracking so previous chunk's silence doesn't carry over
  }, [sendChunkForTranscription]);

  // Update the ref after defining the callback
  useEffect(() => {
    startChunkRecorderRef.current = startChunkRecorder;
  }, [startChunkRecorder]);

  // End current chunk and start a new one
  const triggerChunkEnd = useCallback(() => {
    if (!isRecordingRef.current) {
      return;
    }

    const now = Date.now();
    const chunkDuration = now - chunkStartTimeRef.current;

    // Don't send if chunk is too short
    if (chunkDuration < MIN_CHUNK_DURATION_MS) {
      return;
    }

    // Capture speech state for the onstop handler
    pendingChunkHadSpeechRef.current = hadSpeechInChunkRef.current;

    const currentRecorder = chunkRecorderRef.current;
    if (currentRecorder && currentRecorder.state !== "inactive") {
      // Request final data and stop - onstop handler will process and start new recorder
      currentRecorder.requestData();
      currentRecorder.stop();
    } else if (isRecordingRef.current) {
      // No active recorder, start a new one
      startChunkRecorder();
    }

    // Reset max duration timeout
    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
    }
    maxDurationTimeoutRef.current = setTimeout(() => {
      triggerChunkEndRef.current?.();
    }, MAX_CHUNK_DURATION_MS);
  }, [startChunkRecorder]);

  // Update the ref after defining the callback
  useEffect(() => {
    triggerChunkEndRef.current = triggerChunkEnd;
  }, [triggerChunkEnd]);

  // Audio analysis for VAD and visualization
  const startAudioAnalysis = useCallback(() => {
    const analyser = analyserRef.current;
    if (!analyser) {
      return;
    }

    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    const timeDomainData = new Uint8Array(analyser.fftSize);

    const analyze = () => {
      if (!analyserRef.current || !isRecordingRef.current) {
        return;
      }

      analyserRef.current.getByteTimeDomainData(timeDomainData);
      analyserRef.current.getByteFrequencyData(frequencyData);

      // Calculate RMS for silence detection
      let sum = 0;
      for (const sample of timeDomainData) {
        const normalized = (sample - 128) / 128;
        sum += normalized * normalized;
      }
      const rms = Math.sqrt(sum / timeDomainData.length);
      const db = rms === 0 ? -100 : 20 * Math.log10(rms);

      // Check for silence
      const isQuiet = db < SILENCE_THRESHOLD_DB;
      const now = Date.now();

      if (isQuiet) {
        if (silenceStartRef.current === null) {
          silenceStartRef.current = now;
        } else {
          const silenceDuration = now - silenceStartRef.current;
          const chunkDuration = now - chunkStartTimeRef.current;

          // Trigger chunk end if silence detected and we have enough audio
          if (silenceDuration >= SILENCE_TRIGGER_MS && chunkDuration >= MIN_CHUNK_DURATION_MS) {
            triggerChunkEnd();
            silenceStartRef.current = null;
          }
        }
      } else {
        // Sound detected - mark that this chunk has speech
        hadSpeechInChunkRef.current = true;
        hadAnySpeechRef.current = true; // Track across entire session
        silenceStartRef.current = null;
      }

      // Update visualization levels (8 bars for more dynamic display)
      const numBars = 8;

      let newLevels: number[];

      if (isQuiet) {
        // Decay smoothly toward zero
        newLevels = smoothedLevelsRef.current.map((level) => level * AUDIO_LEVEL_DECAY_RATE);
      } else {
        // Focus on voice frequency range (~80Hz to 3kHz)
        const voiceStartBin = 2;
        const voiceEndBin = 64;
        const binsPerBar = Math.floor((voiceEndBin - voiceStartBin) / numBars);

        newLevels = smoothedLevelsRef.current.map((prevLevel, i) => {
          const startBin = voiceStartBin + i * binsPerBar;
          const endBin = startBin + binsPerBar;

          // Use max in band for responsive peaks
          let bandMax = 0;
          for (let j = startBin; j < endBin; j++) {
            bandMax = Math.max(bandMax, frequencyData[j]);
          }
          const normalized = bandMax / 255;
          // More moderate scaling
          const targetLevel = Math.min(1, Math.pow(normalized, 0.4) * 1.2);

          // Smooth attack and decay
          return targetLevel > prevLevel
            ? prevLevel + (targetLevel - prevLevel) * AUDIO_LEVEL_ATTACK_RATE
            : prevLevel * AUDIO_LEVEL_DECAY_RATE + targetLevel * (1 - AUDIO_LEVEL_DECAY_RATE);
        });
      }

      smoothedLevelsRef.current = newLevels;
      setAudioLevels(newLevels);

      animationFrameRef.current = requestAnimationFrame(analyze);
    };

    animationFrameRef.current = requestAnimationFrame(analyze);
  }, [triggerChunkEnd]);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      setError(null);
      setTranscript("");
      mainChunksRef.current = [];
      transcriptPartsRef.current = [];
      silenceStartRef.current = null;
      hadSpeechInChunkRef.current = false;
      hadAnySpeechRef.current = false;
      isRecordingRef.current = true;
      isCancelledRef.current = false;
      isFinalTranscriptSentRef.current = false;

      setState("recording");

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      // Set up audio analysis
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;

      // Start MAIN recorder (for full recording)
      const mainRecorder = new MediaRecorder(stream);
      mainRecorderRef.current = mainRecorder;
      mimeTypeRef.current = mainRecorder.mimeType || "audio/webm";

      mainRecorder.ondataavailable = (e) => {
        if (e.data.size > 0 && !isCancelledRef.current) {
          mainChunksRef.current.push(e.data);
        }
      };

      mainRecorder.start(DATA_AVAILABLE_INTERVAL_MS);

      // Start first CHUNK recorder (for progressive transcription)
      startChunkRecorder();

      // Set up max duration timeout
      maxDurationTimeoutRef.current = setTimeout(() => {
        triggerChunkEndRef.current?.();
      }, MAX_CHUNK_DURATION_MS);

      // Start audio analysis for VAD
      startAudioAnalysis();
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : "Failed to start recording";
      setError(errorMessage);
      setState("error");
      isRecordingRef.current = false;

      // Clean up any partially allocated resources to prevent leaks
      // (e.g., if getUserMedia succeeded but MediaRecorder failed)
      if (sourceRef.current) {
        sourceRef.current.disconnect();
        sourceRef.current = null;
      }
      if (analyserRef.current) {
        analyserRef.current.disconnect();
        analyserRef.current = null;
      }
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        void audioContextRef.current.close();
        audioContextRef.current = null;
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      onErrorRef.current?.(error_ instanceof Error ? error_ : new Error(errorMessage));
    }
  }, [startChunkRecorder, startAudioAnalysis]);

  // Stop recording and get final transcription
  const stopRecording = useCallback(async (): Promise<string> => {
    isRecordingRef.current = false;

    // Stop animation frame
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    // Clear timeouts
    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }

    // Stop chunk recorder
    if (chunkRecorderRef.current && chunkRecorderRef.current.state !== "inactive") {
      chunkRecorderRef.current.stop();
    }

    // Stop main recorder
    if (mainRecorderRef.current && mainRecorderRef.current.state !== "inactive") {
      mainRecorderRef.current.requestData();
      await new Promise((resolve) => setTimeout(resolve, STOP_REQUEST_DATA_DELAY_MS));
      // Re-check state after await - another call may have already stopped the recorder
      // Access ref fresh to avoid TypeScript narrowing from outer check
      const recorder = mainRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.stop();
      }
    }

    // Wait for final data
    await new Promise((resolve) => setTimeout(resolve, STOP_FINAL_DATA_DELAY_MS));

    setState("processing");

    // Mark that we're about to send the final transcript - this prevents any pending
    // chunk transcriptions from overwriting it (fixes race condition)
    isFinalTranscriptSentRef.current = true;

    // Track the final transcript to return
    let finalTranscript = transcriptPartsRef.current.join(" ");

    // Send the FULL recording for final transcription (only if speech was detected)
    if (mainChunksRef.current.length > 0 && !isCancelledRef.current && hadAnySpeechRef.current) {
      const fullBlob = new Blob(mainChunksRef.current, { type: mimeTypeRef.current });

      try {
        const finalText = await transcribeAudioRef.current(fullBlob);
        if (finalText && finalText.trim()) {
          // Replace progressive transcript with final clean version
          finalTranscript = finalText.trim();
          updateTranscript(finalTranscript);
        }
      } catch {
        // Keep the progressive transcript if final fails
      }
    }

    // Cleanup
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      await audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    setAudioLevels([0, 0, 0, 0, 0, 0, 0, 0]);
    setState("idle");

    return finalTranscript;
  }, [updateTranscript]);

  // Cancel recording
  const cancelRecording = useCallback(() => {
    isRecordingRef.current = false;
    isCancelledRef.current = true;

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (maxDurationTimeoutRef.current) {
      clearTimeout(maxDurationTimeoutRef.current);
      maxDurationTimeoutRef.current = null;
    }

    if (chunkRecorderRef.current && chunkRecorderRef.current.state !== "inactive") {
      chunkRecorderRef.current.stop();
    }

    if (mainRecorderRef.current && mainRecorderRef.current.state !== "inactive") {
      mainRecorderRef.current.stop();
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (analyserRef.current) {
      analyserRef.current.disconnect();
      analyserRef.current = null;
    }
    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      void audioContextRef.current.close();
      audioContextRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }

    mainChunksRef.current = [];
    transcriptPartsRef.current = [];
    setAudioLevels([0, 0, 0, 0, 0, 0, 0, 0]);
    setTranscript("");
    setState("idle");
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isRecordingRef.current = false;
      isCancelledRef.current = true;

      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (maxDurationTimeoutRef.current) {
        clearTimeout(maxDurationTimeoutRef.current);
      }
      if (chunkRecorderRef.current && chunkRecorderRef.current.state !== "inactive") {
        chunkRecorderRef.current.stop();
      }
      if (mainRecorderRef.current && mainRecorderRef.current.state !== "inactive") {
        mainRecorderRef.current.stop();
      }
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        void audioContextRef.current.close();
      }
    };
  }, []);

  return {
    state,
    transcript,
    error,
    audioLevels,
    startRecording,
    stopRecording,
    cancelRecording,
  };
};
